import { errHof, httpDelete, httpGet, httpPost, httpPut } from "@/http"
import { createAsyncThunk } from "@reduxjs/toolkit"

/** @typedef {import("./types").ContentPost} ContentPost */
/** @typedef {import("./types").ContentLink} ContentLink */

export const fetchContentPosts = createAsyncThunk(
  "contentPage/fetchPosts",
  async (_, { rejectWithValue, signal }) => {
    try {
      const data = await httpGet(
        "admin/contentPage/post",
        {},
        { signal, error: errHof(rejectWithValue, "fetchContentPosts") },
      )

      /** @type {ContentPost[]} */
      const posts = data.posts

      return posts
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchContentLinks = createAsyncThunk(
  "contentPage/fetchLinks",
  async (_, { rejectWithValue, signal }) => {
    try {
      const data = await httpGet(
        "admin/contentPage/link",
        {},
        {
          error: errHof(rejectWithValue, "fetchContentLinks"),
          signal,
        },
      )

      /** @type {ContentLink[]} */
      const links = data.links

      return links
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const deleteContentPost = createAsyncThunk(
  "contentPage/deleteContentPost",
  /** @return {number} */
  async ({ postId, onSuccess, onError }, { rejectWithValue, signal }) => {
    try {
      const data = await httpDelete(
        `admin/contentPage/post/${postId}`,
        {},
        {
          success: onSuccess,
          error: errHof(onError, "deleteContentPost", rejectWithValue),
          signal,
        },
      )

      return data.deletedPostId
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const deleteContentLink = createAsyncThunk(
  "contentPage/deleteContentLink",
  /** @returns {number} */
  async ({ linkId, onSuccess, onError }, { rejectWithValue, signal }) => {
    try {
      const data = await httpDelete(
        `admin/contentPage/link/${linkId}`,
        {},
        {
          success: onSuccess,
          error: errHof(onError, "deleteContentLink", rejectWithValue),
          signal,
        },
      )

      return data.deletedLinkId
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const addContentLink = createAsyncThunk(
  "contentPage/addContentLink",
  /** @returns {ContentLink} */
  async ({ data, onSuccess, onError }, { rejectWithValue, signal }) => {
    try {
      const respData = await httpPost("admin/contentPage/link", data, {
        success: onSuccess,
        error: errHof(onError, "addContentLink", rejectWithValue),
        signal,
      })

      return respData.newLink
    } catch (err) {
      rejectWithValue(err)
    }
  },
)

export const addContentPost = createAsyncThunk(
  "contentPage/addContentPost",
  /** @returns {ContentPost} */
  async ({ data, onSuccess, onError }, { rejectWithValue, signal }) => {
    try {
      const respData = await httpPost("admin/contentPage/post", data, {
        success: onSuccess,
        error: errHof(onError, "addContentPost", rejectWithValue),
        signal,
      })

      return respData.newPost
    } catch (err) {
      rejectWithValue(err)
    }
  },
)

export const updateContentPost = createAsyncThunk(
  "contentPage/updateContentPost",
  /** @returns {ContentPost} */
  async ({ data, onSuccess, onError }, { rejectWithValue, signal }) => {
    try {
      const respData = await httpPut("admin/contentPage/post", data, {
        success: onSuccess,
        error: errHof(onError, "updateContentPost", rejectWithValue),
        signal,
      })

      return respData.updatedPost
    } catch (err) {
      rejectWithValue(err)
    }
  },
)
